/**
 * Booth Artwork Studio — client service.
 *
 * Collections:
 *   artwork_projects/{projectId}
 *   artwork_projects/{projectId}/sources/{sourceId}    uploaded brand art
 *   artwork_projects/{projectId}/surfaces/{surfaceId}  one per deliverable
 *
 * Invariants worth knowing before editing this file:
 *
 * - Storage is server-write-only. Brand art goes up as data URLs through the
 *   uploadArtworkSources callable, which re-encodes it with sharp. There is no
 *   client-side uploadBytes path here and storage.rules denies one.
 * - The surface registry lives in the cloud repo, not here. listSurfaces()
 *   fetches it. Never hardcode a dimension in the UI.
 * - Superadmin only, enforced in firestore.rules and again in every callable.
 */

import {
  collection,
  collectionGroup,
  deleteDoc,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../config/firebase';

const PROJECTS = 'artwork_projects';

/** Strip undefined — Firestore rejects it, and patches are built conditionally. */
const cleanPatch = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

const call = (name, opts) => httpsCallable(functions, name, opts);

/* ------------------------------------------------------------------ projects */

export async function listProjects({ pageSize = 50 } = {}) {
  const snap = await getDocs(
    query(collection(db, PROJECTS), orderBy('updated_at', 'desc'), limit(pageSize))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getProject(projectId) {
  const snap = await getDoc(doc(db, PROJECTS, projectId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function createProject({ name, organizationId = null, boothId = null }) {
  const ref = await addDoc(collection(db, PROJECTS), {
    name: (name || '').trim().slice(0, 200) || 'Untitled booth artwork',
    organizationId,
    boothId,
    status: 'brand',
    brandKit: null,
    createdBy: auth.currentUser?.uid || null,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return ref.id;
}

export async function updateProject(projectId, patch) {
  await updateDoc(doc(db, PROJECTS, projectId), {
    ...cleanPatch(patch),
    updated_at: serverTimestamp(),
  });
}

export async function deleteProject(projectId) {
  // Storage objects under booth-artwork/{projectId}/ are left in place — they
  // are server-owned and cheap, and a client-side recursive delete would be a
  // long unbounded loop. Clean them with a lifecycle rule if it ever matters.
  await deleteDoc(doc(db, PROJECTS, projectId));
}

/* ------------------------------------------------------------------- sources */

export async function listSources(projectId, { pageSize = 40 } = {}) {
  const snap = await getDocs(
    query(collection(db, PROJECTS, projectId, 'sources'), limit(pageSize))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Read files as data URLs and hand them to the callable.
 *
 * Deliberately not a direct Storage write: the server re-encodes every image
 * (strips EXIF, caps dimensions, rejects decompression bombs) before anything
 * reaches a model prompt.
 */
export async function uploadSources(projectId, files, { onProgress } = {}) {
  const images = [];
  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    if (onProgress) onProgress(`Reading ${i + 1} of ${files.length}…`);
    images.push({
      name: file.name,
      kind: file.kind || 'collateral',
      dataUrl: await fileToDataUrl(file.file || file),
    });
  }
  if (onProgress) onProgress('Uploading…');
  // Large payload plus server-side re-encode; well past the SDK's 70s default.
  const res = await call('uploadArtworkSources', { timeout: 300000 })({ projectId, images });
  return res.data.sources;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/* ------------------------------------------------------------------ surfaces */

/**
 * Which uploaded image is the venue's mark.
 *
 * Exactly one source is the logo, so promoting one demotes the rest. This is
 * the fix for a venue screenshot being composited onto every panel with no way
 * to correct it.
 */
export async function setLogoSource(projectId, sourceId) {
  const sources = await listSources(projectId);
  await Promise.all(
    sources.map((s) =>
      updateDoc(doc(db, PROJECTS, projectId, 'sources', s.id), {
        kind: s.id === sourceId ? 'logo' : 'collateral',
      })
    )
  );
}

/**
 * Remove an uploaded source.
 *
 * The Storage object is left behind deliberately — it is server-owned, cheap,
 * and a client-side delete would need write access to a path that denies it.
 */
export async function removeSource(projectId, sourceId) {
  await deleteDoc(doc(db, PROJECTS, projectId, 'sources', sourceId));
}

/**
 * Throw a surface away and start it over.
 *
 * Deletes the whole document rather than clearing fields, so the next prompt
 * generation treats it as new. Renders and outputs stay in Storage but are no
 * longer referenced.
 */
export async function resetSurface(projectId, surfaceId) {
  await deleteDoc(doc(db, PROJECTS, projectId, 'surfaces', surfaceId));
}

/** Clear every surface on a project, keeping the brand kit and sources. */
export async function resetAllSurfaces(projectId) {
  const surfaces = await listProjectSurfaces(projectId);
  await Promise.all(surfaces.map((s) => deleteDoc(doc(db, PROJECTS, projectId, 'surfaces', s.id))));
}

/**
 * The surface registry, plus which generation mode the studio is in.
 * Render sizes depend on the configured image model, so this must come from the
 * server rather than being assumed here.
 */
export async function listSurfaces() {
  const res = await call('listArtworkSurfaces')();
  return {
    surfaces: res.data.surfaces,
    imageModel: res.data.imageModel,
    imageApiConfigured: !!res.data.imageApiConfigured,
  };
}

export async function listProjectSurfaces(projectId, { pageSize = 20 } = {}) {
  const snap = await getDocs(
    query(collection(db, PROJECTS, projectId, 'surfaces'), limit(pageSize))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Live subscription to a project's surfaces.
 *
 * A render or a compose outlives the request that started it, so status has to
 * come from the documents rather than from the callable's return value. Caller
 * must invoke the returned unsubscribe in its effect cleanup.
 */
export function watchProjectSurfaces(projectId, onChange, onError) {
  return onSnapshot(
    query(collection(db, PROJECTS, projectId, 'surfaces'), limit(20)),
    (snap) => onChange(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.error('[BoothArtwork] surfaces subscription error:', err);
      if (onError) onError(err);
    }
  );
}

/** Save a reviewer's edit to a prompt. The edited text wins at render time. */
export async function saveEditedPrompt(projectId, surfaceId, text) {
  await updateDoc(doc(db, PROJECTS, projectId, 'surfaces', surfaceId), {
    'prompt.edited': text,
    updated_at: serverTimestamp(),
  });
}

/** Choose which render composes. Lets you go back to an earlier attempt. */
export async function selectRender(projectId, surfaceId, renderId) {
  await updateDoc(doc(db, PROJECTS, projectId, 'surfaces', surfaceId), {
    selectedRenderId: renderId,
    updated_at: serverTimestamp(),
  });
}

/**
 * Count surfaces across every project that are finished or waiting on a look.
 *
 * Drives the nav badge, so a superadmin can start a render, navigate away, and
 * be told when it lands. Collection-group scope needs both an index and a rule
 * declared at that scope — see socialsnap-cloud firestore.indexes.json and the
 * `{path=**}/surfaces` rule.
 */
export function watchArtworkAttention(onCount, onError) {
  return onSnapshot(
    query(
      collectionGroup(db, 'surfaces'),
      where('status', 'in', ['awaiting_review', 'ready']),
      limit(100)
    ),
    (snap) => {
      let awaitingReview = 0;
      let ready = 0;
      snap.docs.forEach((d) => {
        if (d.data().status === 'ready') ready += 1;
        else awaitingReview += 1;
      });
      onCount({ awaitingReview, ready, total: awaitingReview + ready });
    },
    (err) => {
      // Missing index or rules not yet deployed — the badge is advisory, so a
      // failure here must never break the nav.
      console.warn('[BoothArtwork] attention count unavailable:', err?.message);
      if (onError) onError(err);
      onCount({ awaitingReview: 0, ready: 0, total: 0 });
    }
  );
}

/* ----------------------------------------------------------------- pipeline */

export async function analyzeBrandKit(projectId) {
  const res = await call('analyzeBrandKit', { timeout: 300000 })({ projectId });
  return res.data.brandKit;
}

export async function generatePrompts(projectId, { surfaceIds = null, regenerate = false } = {}) {
  const res = await call('generateSurfacePrompts', { timeout: 300000 })(
    cleanPatch({ projectId, surfaceIds: surfaceIds || undefined, regenerate })
  );
  return res.data.surfaces;
}

export async function renderSurface(projectId, surfaceId, { refinement = '' } = {}) {
  // High-quality generation runs well past a minute.
  const res = await call('renderSurface', { timeout: 540000 })(
    cleanPatch({ projectId, surfaceId, refinement: refinement || undefined })
  );
  return res.data;
}

/**
 * Upload a plate generated elsewhere.
 *
 * The recovery path when the API refuses a prompt on content policy, rate
 * limits mid-project, or simply keeps missing on one surface and you want a
 * tool with interactive editing. The plate is treated identically to an API
 * render from here on.
 *
 * Sent as a data URL for the same reason brand art is: Storage denies client
 * writes to the project tree. Capped at 18MB by the callable — for a very large
 * plate, export a high-quality JPEG rather than a PNG.
 */
export async function addManualRender(projectId, surfaceId, file, { note = '' } = {}) {
  const dataUrl = await fileToDataUrl(file);
  const res = await call('addManualRender', { timeout: 300000 })({
    projectId,
    surfaceId,
    dataUrl,
    note,
  });
  return res.data.render;
}

export async function composeSurface(projectId, surfaceId, { renderId = null, withLogo = true } = {}) {
  // 84 megapixels of compositing plus an ICC transform.
  const res = await call('composeSurface', { timeout: 540000 })(
    cleanPatch({ projectId, surfaceId, renderId: renderId || undefined, withLogo })
  );
  return res.data;
}

/**
 * Render a seamless looping MP4 from a composed screen plate.
 *
 * Idle and signage only. Runs long — a 1920x1080 loop is around 30 seconds of
 * encoding — and like every other step it survives the page being closed.
 */
export async function animateSurface(projectId, surfaceId, { taper, sway, seconds } = {}) {
  const res = await call('animateSurface', { timeout: 540000 })(
    cleanPatch({ projectId, surfaceId, taper, sway, seconds })
  );
  return res.data;
}

export async function applyToScreenTheme(projectId, surfaceId, { screenThemeId = null, themeName = null } = {}) {
  const res = await call('applyArtworkToScreenTheme')(
    cleanPatch({
      projectId,
      surfaceId,
      screenThemeId: screenThemeId || undefined,
      themeName: themeName || undefined,
    })
  );
  return res.data;
}
